// ForgotPassword.cshtml (the same page in both apps): a code by e-mail, then the code and a new password. The code is
// never shown on screen.
import { api, APP, BffError, GENERIC_ERROR } from '../bff';
import { useBodyClass } from '../dom';
import { useBrand, useTitle } from '../session';
import { useState, type FormEvent, type MouseEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PasswordField } from '../PasswordInput';
import { ConsoleShowcase } from './ConsoleShowcase';
import { PaymentShowcase, SPLIT, type ShowcaseKind } from './PaymentShowcase';

const errorText = (err: unknown) => (err instanceof BffError ? err.message : GENERIC_ERROR);

export function ForgotPasswordPage({ titleSuffix = '', showcase }: { titleSuffix?: string; showcase?: ShowcaseKind }) {
  const [params] = useSearchParams();
  const brand = useBrand(null);
  useTitle('Set or Reset Password', { displayName: `${brand.displayName}${titleSuffix}` });
  useBodyClass('qor-login');
  const navigate = useNavigate();
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function requestReset(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api('POST', '/auth/password/forgot', { body: { app: APP, email: email.trim() }, noRefresh: true });
      setEmail(email.trim());
      setError(null);
      setCodeSent(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function reset(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      if (password !== confirmPassword) {
        setError("Passwords don't match.");
        setBusy(false);
        return;
      }
      await api('POST', '/auth/password/reset', {
        body: { app: APP, email, code: code.replace(/\s/g, ''), newPassword: password },
        noRefresh: true,
      });
      navigate(`/login?reset=1&email=${encodeURIComponent(email)}`, { replace: true });
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  function change(e: MouseEvent) {
    e.preventDefault();
    setCodeSent(false);
    setCode('');
    setPassword('');
    setConfirmPassword('');
    setError(null);
  }

  return (
    <main className={SPLIT[showcase ?? 'none']}>
      <div className="form-panel">
        <div className="form-inner">
          <Link className="brand" to="/login" aria-label={`${brand.displayName} home`}>
            <img src="/images/qor-logo-mark.png" alt={brand.displayName} />
          </Link>

          <div className="heading">
            <p className="eyebrow">{showcase === 'console' ? 'Staff access' : 'Account access'}</p>
            <h1>Set or reset your password</h1>
            {!codeSent && <p>Enter your email and we&apos;ll send you a code, for a first password or a new one.</p>}
          </div>

          {error && (
            <div className="alert alert-error" role="alert">
              {error}
            </div>
          )}

          {!codeSent ? (
            <form onSubmit={(e) => void requestReset(e)}>
              <label className="field">
                <span>Email</span>
                <input
                  type="email"
                  name="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  autoComplete="username"
                  required
                  autoFocus
                />
              </label>
              <button type="submit" className="submit" disabled={busy}>
                Send code
              </button>
            </form>
          ) : (
            <>
              <div className="email-summary">
                <span>{email}</span>
                <a className="link" href="/forgot-password" onClick={change}>
                  Change
                </a>
              </div>
              <p className="note">We&apos;ve emailed a verification code to {email}.</p>

              <form onSubmit={(e) => void reset(e)}>
                <label className="field">
                  <span>Code</span>
                  <input
                    type="text"
                    name="ResetCode"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Enter code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    autoFocus
                  />
                </label>
                <PasswordField
                  className="field"
                  label="New password"
                  name="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 10 characters"
                  autoComplete="new-password"
                  required
                  minLength={10}
                />
                <PasswordField
                  className="field"
                  label="Confirm new password"
                  name="ConfirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  autoComplete="new-password"
                  required
                  minLength={10}
                />
                <button type="submit" className="submit" disabled={busy}>
                  Set password
                </button>
              </form>
            </>
          )}

          <p className="hint">
            <Link className="link" to="/login">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
      {showcase === 'console' ? (
        <ConsoleShowcase />
      ) : showcase ? (
        <PaymentShowcase kind={showcase} />
      ) : (
        <div className="hero-panel">
          <img
            className="hero-logo"
            src="/images/qor-logo-overlay.png"
            alt={`${brand.displayName} - The future is here`}
          />
        </div>
      )}
    </main>
  );
}
